<template>
    <div class="page-container">
        <div class="flex items-center gap-4 mb-8">
            <div class="bg-indigo-600 p-3 rounded-2xl shadow-lg">
                <LucideIcon :size="24" class="text-white">
                    <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"/><polyline points="14 2 14 8 20 8"/>
                </LucideIcon>
            </div>
            <div>
                <h2 class="page-title text-2xl font-bold text-slate-900">{{ $t('household.statements.title') }}</h2>
                <p class="section-subtitle text-slate-500">{{ $t('household.statements.subtitle') }}</p>
            </div>
        </div>

        <div v-if="loading" class="flex justify-center py-20">
            <div class="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600"></div>
        </div>

        <template v-else>
            <!-- Карта выгрузки -->
            <div class="statement-card full-width overflow-hidden mb-8">
                <div class="px-8 py-6 border-b border-slate-100 bg-slate-50/50">
                    <h3 class="font-extrabold text-slate-900 text-lg uppercase tracking-tight">{{ $t('household.shared.makeExport') }}</h3>
                </div>
                <div class="p-8">
                    <div class="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
                        <div class="form-group">
                            <label class="form-label">{{ $t('household.statements.warehouseLabel') }}</label>
                            <select v-model="selectedWarehouse" class="form-select" @change="onWarehouseChange">
                                <option value="">{{ $t('household.shared.allWarehouses') }}</option>
                                <option v-for="w in warehouses" :key="w.id" :value="w.id">{{ w.name }}</option>
                            </select>
                        </div>
                        <div class="form-group">
                            <label class="form-label">{{ $t('household.statements.zoneLabel') }}</label>
                            <select v-model="selectedZone" class="form-select" :disabled="!selectedWarehouse">
                                <option value="">{{ $t('household.shared.allZones') }}</option>
                                <option v-for="z in zones" :key="z.id" :value="z.id">{{ z.name }}</option>
                            </select>
                        </div>
                        <div class="form-group">
                            <label class="form-label">{{ $t('household.shared.dateFrom') }}</label>
                            <DatePicker v-model="dateFrom" format="iso" class="form-input" />
                        </div>
                        <div class="form-group">
                            <label class="form-label">{{ $t('household.shared.dateTo') }}</label>
                            <DatePicker v-model="dateTo" format="iso" class="form-input" />
                        </div>
                    </div>
                    <button
                        @click="handleExport"
                        class="flex items-center gap-2 px-8 py-3 bg-green-600 text-white font-bold rounded-xl hover:bg-green-700 transition-all shadow-md active:scale-95"
                    >
                        <LucideIcon :size="18"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></LucideIcon>
                        {{ $t('household.shared.exportBtn') }}
                    </button>
                </div>
            </div>

            <!-- Оборотная ведомость -->
            <div class="statement-card full-width overflow-hidden">
                <div class="px-8 py-6 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
                    <h3 class="font-extrabold text-slate-900 text-lg uppercase tracking-tight">{{ $t('household.statements.monthlyTurnoverTitle') }}</h3>
                </div>
                <div class="p-8">
                    <div class="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
                        <div class="balance-card bg-blue-50 border border-blue-100/50 shadow-sm">
                            <div class="flex items-center justify-between">
                                <div>
                                    <p class="balance-label text-blue-600">{{ $t('household.statements.openingBalance') }}</p>
                                    <p class="balance-value text-blue-900">{{ formatPrice(openingBalance) }}</p>
                                </div>
                                <div class="bg-blue-600 text-white p-2.5 rounded-xl shadow-lg shadow-blue-200">
                                    <LucideIcon :size="24"><path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"/><polyline points="14 2 14 8 20 8"/></LucideIcon>
                                </div>
                            </div>
                        </div>
                        <div class="balance-card bg-emerald-50 border border-emerald-100/50 shadow-sm">
                            <div class="flex items-center justify-between">
                                <div>
                                    <p class="balance-label text-emerald-600">{{ $t('household.statements.receiptLabel') }}</p>
                                    <p class="balance-value text-emerald-900">{{ formatPrice(receiptSum) }}</p>
                                    <p class="text-[10px] text-emerald-600 mt-2 font-extrabold uppercase tracking-widest">{{ monthlyReceipts.length }} {{ $t('household.statements.opsCountSuffix') }}</p>
                                </div>
                                <div class="bg-emerald-600 text-white p-2.5 rounded-xl shadow-lg shadow-emerald-200">
                                    <LucideIcon :size="24"><path d="m22 7-8.5 8.5-5-5L2 17"/><path d="M16 7h6v6"/></LucideIcon>
                                </div>
                            </div>
                        </div>
                        <div class="balance-card bg-rose-50 border border-rose-100/50 shadow-sm">
                            <div class="flex items-center justify-between">
                                <div>
                                    <p class="balance-label text-rose-600">{{ $t('household.statements.consumptionLabel') }}</p>
                                    <p class="balance-value text-rose-900">{{ formatPrice(consumptionSum) }}</p>
                                    <p class="text-[10px] text-rose-600 mt-2 font-extrabold uppercase tracking-widest">{{ monthlyConsumptions.length }} {{ $t('household.statements.opsCountSuffix') }}</p>
                                </div>
                                <div class="bg-rose-600 text-white p-2.5 rounded-xl shadow-lg shadow-emerald-200">
                                    <LucideIcon :size="24"><path d="m22 17-8.5-8.5-5 5L2 7"/><path d="M16 17h6v-6"/></LucideIcon>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="bg-slate-50 rounded-2xl p-6 border border-slate-200 flex items-center justify-between">
                        <div>
                            <p class="text-sm font-bold text-slate-500 mb-1 uppercase tracking-tight">{{ $t('household.statements.closingBalance') }}</p>
                            <p class="text-4xl font-extrabold text-slate-900 tracking-tighter">{{ formatPrice(closingBalance) }}</p>
                        </div>
                        <div class="bg-indigo-600 h-12 w-1.5 rounded-full"></div>
                    </div>
                </div>
            </div>
        </template>
    </div>
</template>


<style scoped>
.page-container { padding: 2rem; background: #f3f3f5; min-height: 100%; }
.page-title { color: #000000; font-size: 1.75rem; font-weight: 700; margin: 0; }
.section-subtitle { color: #64748b; font-size: 0.875rem; margin-top: 0.25rem; }

.statement-card {
    background: white;
    border-radius: 1.5rem;
    border: 1px solid #eef0f4;
    box-shadow: 0 4px 6px -1px rgba(0,0,0,0.02), 0 2px 4px -1px rgba(0,0,0,0.01);
    margin-bottom: 2rem;
}
.card-header { padding: 1.5rem 2rem 0; }

.balance-card {
    padding: 1.5rem;
    border-radius: 1.25rem;
}
.balance-label { font-size: 0.7rem; font-weight: 800; text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 0.5rem; }
.balance-value { font-size: 1.75rem; font-weight: 800; letter-spacing: -0.025em; }

.info-chunk p { font-size: 0.6rem; font-weight: 800; color: #94a3b8; text-transform: uppercase; margin-bottom: 0.25rem; letter-spacing: 0.05em; }
.info-chunk span { font-size: 1rem; font-weight: 900; color: #0f172a; }

.top-item-row:hover { transform: translateX(4px); }

.form-group { display: flex; flex-direction: column; }
.form-label { font-size: 0.75rem; font-weight: 700; color: #64748b; text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 0.5rem; }
.form-select, .form-input {
    background: white;
    border: 1px solid #e2e8f0;
    border-radius: 0.75rem;
    padding: 0.75rem 1rem;
    font-size: 0.9rem;
    color: #1e293b;
    outline: none;
    transition: all 0.2s;
}
.form-select:focus, .form-input:focus { border-color: #6366f1; box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1); }
.form-select:disabled { background: #f8fafc; color: #94a3b8; cursor: not-allowed; }
.mb-8 { margin-bottom: 2rem; }
</style>

<script setup>
import { ref, onMounted, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import LucideIcon from '@/components/common/LucideIcon.vue';

const { t } = useI18n();
import { getStatements, getStockReport } from '@/api/household_reports';
import { getOperations } from '@/api/household_operations';
import { getNomenclatures } from '@/api/household_nomenclatures';
import { getWarehouses, getWarehouseZones } from '@/api/household_warehouses';
import { http } from '@/api/http';
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import DatePicker from '@/components/widgets/DatePicker.vue';

const loading = ref(true);
const stock = ref([]);
const operations = ref([]);
const nomenclatures = ref([]);
const warehouses = ref([]);
const zones = ref([]);
const selectedWarehouse = ref('');
const selectedZone = ref('');
const dateFrom = ref('');
const dateTo = ref('');

const fetchData = async () => {
    loading.value = true;
    try {
        const [statementsRes, stockRes, opRes, nomRes, whRes] = await Promise.all([
            getStatements(),
            getStockReport(),
            getOperations(),
            getNomenclatures(),
            getWarehouses()
        ]);
        stock.value = Array.isArray(stockRes.data) ? stockRes.data : (stockRes.data?.results || []);
        operations.value = (Array.isArray(opRes.data) ? opRes.data : (opRes.data?.results || [])).map(op => ({
            ...op,
            items: op.nomenclatures || op.items || [] // Handle WMS names if needed
        }));
        nomenclatures.value = Array.isArray(nomRes.data) ? nomRes.data : (nomRes.data?.results || []);
        warehouses.value = Array.isArray(whRes.data) ? whRes.data : (whRes.data?.results || []);
    } catch (error) {
        console.error('Error fetching statements data:', error);
    } finally {
        loading.value = false;
    }
};

const lastMonth = computed(() => {
    const d = new Date();
    d.setMonth(d.getMonth() - 1);
    return d;
});

const monthlyReceipts = computed(() => {
    return operations.value.filter(op => op.type === 'receipt' && new Date(op.date) >= lastMonth.value);
});

const monthlyConsumptions = computed(() => {
    return operations.value.filter(op => op.type === 'consumption' && new Date(op.date) >= lastMonth.value);
});

const receiptSum = computed(() => {
    return monthlyReceipts.value.reduce(
        (sum, op) => sum + op.items.reduce((s, item) => s + Number(item?.cost || 0), 0),
        0
    );
});

const consumptionSum = computed(() => {
    return monthlyConsumptions.value.reduce(
        (sum, op) => sum + op.items.reduce((s, item) => s + Number(item?.cost || 0), 0),
        0
    );
});

const closingBalance = computed(() => {
    return stock.value.reduce((sum, item) => sum + Number(item?.cost || 0), 0);
});

const openingBalance = computed(() => {
    return closingBalance.value - receiptSum.value + consumptionSum.value;
});

const stockByWarehouse = computed(() => {
    return warehouses.value.map(warehouse => {
        const warehouseStock = stock.value.filter(s => s.warehouseId === warehouse.id);
        const totalValue = warehouseStock.reduce((sum, item) => sum + Number(item?.cost || 0), 0);
        const totalItems = warehouseStock.reduce((sum, item) => sum + Number(item?.quantity || 0), 0);
        const uniqueNomenclatures = new Set(warehouseStock.map(s => s.nomenclatureId)).size;

        return {
            warehouse,
            totalValue,
            totalItems,
            uniqueNomenclatures,
            stock: warehouseStock,
        };
    });
});

const topItems = computed(() => {
    return [...stock.value].sort((a, b) => b.cost - a.cost).slice(0, 10);
});

const getNomenclatureName = (id) => nomenclatures.value.find(n => n.id === id)?.name || t('household.shared.unknown');
const getNomenclatureUnit = (id) => nomenclatures.value.find(n => n.id === id)?.unit || '';
const getWarehouseName = (id) => warehouses.value.find(w => w.id === id)?.name || t('household.shared.unknown');

const formatPrice = (price) => {
    return new Intl.NumberFormat('ru-RU', { style: 'currency', currency: 'KZT', maximumFractionDigits: 0 }).format(price);
};


const handleExport = async () => {
    const date_from = dateFrom.value || '2026-01-01';
    const date_to = dateTo.value || new Date().toISOString().split('T')[0];

    if (selectedWarehouse.value && zones.value.length === 0) {
        await onWarehouseChange();
    }

    const zone = selectedZone.value || zones.value?.[0]?.id || null;
    if (!zone) {
        window.alert(t('household.statements.zoneRequired'));
        return;
    }

    try {
                const { data } = await http.get('wms/reports/responsibility-center/', {
            params: {
                warehouse_zone: zone,
                date_from,
                date_to,
            },
        });

            const fixedAssets = Array.isArray(data?.fixed_assets) ? data.fixed_assets : [];
            const inventories = Array.isArray(data?.inventories) ? data.inventories : [];

            if (fixedAssets.length === 0 && inventories.length === 0) {
                window.alert(t('household.statements.noDataZonePeriod'));
                return;
            }

      /* ---------- ШАПКА ---------- */
      const sheetData = [
        [t('household.statements.excelReportTitle')],
        [],
        [t('household.statements.excelWarehouseLabel'), data.warehouse_name],
        [t('household.statements.excelZoneLabel'), data.warehouse_zone_name],
        [t('household.statements.excelPeriodLabel'), `${data.date_from} — ${data.date_to}`],
        [],
      ];

      /* ---------- ОС ---------- */
      sheetData.push(
        [t('household.assetTypes.fixedAssetsSection')],
        [
          t('household.statements.excelColNum'),
          t('household.statements.excelColName'),
          t('household.statements.excelColCode'),
          // "Инв. №", // TODO: Раскомментировать когда backend добавит uchet_number
          t('household.statements.excelColUnit'),
          t('household.statements.excelColPrice'),
          t('household.statements.excelColOpening'),
          t('household.statements.excelColIncome'),
          t('household.statements.excelColExpense'),
          t('household.statements.excelColEnding'),
        ]
      );

            fixedAssets.forEach((item, index) => {
        sheetData.push([
          index + 1,
          item.name,
          item.code,
          // item.uchet_number, // TODO: Раскомментировать когда backend добавит uchet_number
          item.unit,
          Number(item.price),
          Number(item.beginning_balance),
          Number(item.income),
          Number(item.expense),
          Number(item.ending_balance),
        ]);
      });

      sheetData.push([], []);

      /* ---------- ТМЦ ---------- */
      sheetData.push(
        [t('household.assetTypes.inventoriesSection')],
        [
          t('household.statements.excelColNum'),
          t('household.statements.excelColName'),
          t('household.statements.excelColCode'),
          // "Инв. №", // TODO: Раскомментировать когда backend добавит uchet_number
          t('household.statements.excelColUnit'),
          t('household.statements.excelColPrice'),
          t('household.statements.excelColOpening'),
          t('household.statements.excelColIncome'),
          t('household.statements.excelColExpense'),
          t('household.statements.excelColEnding'),
        ]
      );

            inventories.forEach((item, index) => {
        sheetData.push([
          index + 1,
          item.name,
          item.code,
          // item.uchet_number, // TODO: Раскомментировать когда backend добавит uchet_number
          item.unit,
          Number(item.price),
          Number(item.beginning_balance),
          Number(item.income),
          Number(item.expense),
          Number(item.ending_balance),
        ]);
      });

      /* ---------- EXCEL ---------- */
      const worksheet = XLSX.utils.aoa_to_sheet(sheetData);

      worksheet["!cols"] = [
        { wch: 5 },
        { wch: 40 },
        { wch: 12 },
        { wch: 15 },
        { wch: 10 },
        { wch: 12 },
        { wch: 14 },
        { wch: 10 },
        { wch: 10 },
        { wch: 16 },
      ];

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, t('household.statements.excelSheetName'));

      const excelBuffer = XLSX.write(workbook, {
        bookType: "xlsx",
        type: "array",
      });

      const file = new Blob([excelBuffer], {
        type:
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      saveAs(
        file,
        `${t('household.statements.excelFilePrefix')}_${data.warehouse_name}_${data.date_from}_${data.date_to}.xlsx`
      );
    } catch (error) {
        console.error("Ошибка экспорта:", error);
    }
};

const onWarehouseChange = async () => {
    selectedZone.value = '';
    zones.value = [];
    if (selectedWarehouse.value) {
        try {
            const res = await getWarehouseZones({ warehouse: selectedWarehouse.value });
            zones.value = Array.isArray(res.data) ? res.data : (res.data?.results || []);
        } catch (e) {
            console.error('Failed to load zones:', e);
        }
    }
};

onMounted(fetchData);
</script>
